import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { fmtError } from "@/lib/supabase";
import { listFeedback, updateFeedbackStatus } from "@/lib/feedback";
import { Search, X, Check, EyeOff, AlertCircle, Lightbulb, MessageSquare } from "lucide-react";
import Avatar from "@/components/Avatar";

const STATUSES = [
  { key: "open",     label: "Open" },
  { key: "reviewed", label: "Reviewed" },
  { key: "closed",   label: "Closed" },
  { key: "all",      label: "All" },
];
const CATS = [
  { key: "all",     label: "All" },
  { key: "bug",     label: "Bug", Icon: AlertCircle },
  { key: "feature", label: "Feature", Icon: Lightbulb },
  { key: "general", label: "General", Icon: MessageSquare },
];

const CAT_TONE = {
  bug:     "bg-destructive/15 text-destructive border-destructive/40",
  feature: "bg-neon/40 border-ink",
  general: "bg-paper border-ink",
};

export default function AdminFeedback() {
  const [status, setStatus] = useState("open");
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    listFeedback({ status, q })
      .then((r) => setRows(r))
      .catch((e) => toast.error(fmtError(e)))
      .finally(() => setLoading(false));
  }, [status, q]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  // Poll every 30s for new feedback. Cheap & reliable; if it costs too much
  // we can swap to a Supabase realtime channel later.
  useEffect(() => {
    const i = setInterval(load, 30000);
    return () => clearInterval(i);
  }, [load]);

  const filtered = useMemo(
    () => (cat === "all" ? rows : rows.filter((r) => r.category === cat)),
    [rows, cat]
  );
  const openCount = useMemo(
    () => rows.filter((r) => r.status === "open").length,
    [rows]
  );

  const setRowStatus = async (id, next) => {
    try {
      await updateFeedbackStatus(id, next);
      toast.success(next === "closed" ? "Marked closed." : next === "reviewed" ? "Marked reviewed." : "Reopened.");
      setRows((arr) => arr.map((r) => (r.id === id ? { ...r, status: next } : r)));
      if (detail?.id === id) setDetail({ ...detail, status: next });
    } catch (err) { toast.error(fmtError(err)); }
  };

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="text-xs uppercase tracking-[0.3em] text-ink/55">Admin</span>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1 leading-none">
            Feedback inbox
          </h1>
        </div>
        {openCount > 0 && (
          <span data-testid="feedback-open-badge" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-neon border-[2.5px] border-ink rounded-full text-[11px] uppercase tracking-widest font-extrabold">
            {openCount} open
          </span>
        )}
      </header>

      <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search size={14} strokeWidth={2.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/50 pointer-events-none" />
          <input
            type="text" value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search subject or message"
            data-testid="admin-feedback-search"
            className="w-full border-[2.5px] border-ink rounded-full bg-paper pl-9 pr-3 py-2.5 text-sm font-medium outline-none"
          />
        </div>

        {/* Status pills */}
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => {
            const active = status === s.key;
            return (
              <button
                key={s.key} type="button"
                onClick={() => setStatus(s.key)}
                data-testid={`feedback-status-${s.key}`}
                className={`px-3 py-1.5 rounded-full border-[2.5px] border-ink text-[11px] uppercase tracking-widest font-extrabold ${active ? "bg-neon" : "bg-paper hover:bg-ink hover:text-paper transition-colors"}`}
              >{s.label}</button>
            );
          })}
        </div>
      </div>

      {/* Category pills */}
      <div className="flex flex-wrap gap-2 -mt-1">
        {CATS.map((c) => {
          const active = cat === c.key;
          return (
            <button
              key={c.key} type="button"
              onClick={() => setCat(c.key)}
              data-testid={`feedback-cat-${c.key}`}
              className={`px-3 py-1.5 rounded-full border-[2.5px] border-ink text-[11px] uppercase tracking-widest font-extrabold inline-flex items-center gap-1.5 ${active ? "bg-neon" : "bg-paper hover:bg-ink hover:text-paper transition-colors"}`}
            >
              {c.Icon && <c.Icon size={12} strokeWidth={2.6} />} {c.label}
            </button>
          );
        })}
      </div>

      {/* Loading / empty */}
      {loading && <div className="text-center text-ink/55 py-10 text-sm uppercase tracking-widest">Loading…</div>}
      {!loading && filtered.length === 0 && (
        <div data-testid="feedback-empty" className="text-center text-ink/55 py-14 text-sm">No feedback {status === "all" ? "" : `(${status})`} yet.</div>
      )}

      {/* Desktop table */}
      <div className="hidden md:block">
        {!loading && filtered.length > 0 && (
          <div className="border-[2.5px] border-ink rounded-2xl overflow-hidden bg-paper">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-widest text-ink/55 bg-ink/[0.04]">
                <tr>
                  <th className="px-4 py-3">From</th>
                  <th className="px-4 py-3">Subject</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Sent</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} data-testid={`feedback-row-${r.id}`} className="border-t border-ink/10 hover:bg-ink/[0.03] cursor-pointer" onClick={() => setDetail(r)}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Avatar src={r.reporter?.avatar_url} name={r.reporter?.display_name || r.reporter?.username} size="sm" />
                        <span className="font-bold">@{r.reporter?.username || "anon"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 max-w-md truncate">{r.subject}</td>
                    <td className="px-4 py-3"><CatPill cat={r.category} /></td>
                    <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                    <td className="px-4 py-3 text-ink/60 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <RowActions row={r} onSetStatus={setRowStatus} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Mobile cards */}
      <div className="flex md:hidden flex-col gap-3">
        {!loading && filtered.map((r) => (
          <button key={r.id} type="button" data-testid={`feedback-card-${r.id}`} onClick={() => setDetail(r)} className="text-left border-[2.5px] border-ink rounded-2xl bg-paper p-4 flex flex-col gap-2 shadow-pop-sm">
            <div className="flex items-center gap-2">
              <Avatar src={r.reporter?.avatar_url} name={r.reporter?.display_name || r.reporter?.username} size="sm" />
              <span className="font-bold text-sm">@{r.reporter?.username || "anon"}</span>
              <span className="ml-auto"><StatusPill status={r.status} /></span>
            </div>
            <div className="font-extrabold text-base leading-tight line-clamp-1">{r.subject}</div>
            <div className="text-xs text-ink/65 line-clamp-2">{r.message}</div>
            <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-ink/50">
              <CatPill cat={r.category} />
              <span>{new Date(r.created_at).toLocaleDateString()}</span>
            </div>
          </button>
        ))}
      </div>

      {detail && <DetailPanel row={detail} onClose={() => setDetail(null)} onSetStatus={setRowStatus} />}
    </div>
  );
}

function StatusPill({ status }) {
  const tone =
    status === "open"     ? "bg-neon border-ink" :
    status === "reviewed" ? "bg-ink text-paper border-ink" :
                            "bg-paper text-ink/55 border-ink/30";
  return <span className={`inline-flex px-2.5 py-0.5 text-[10px] uppercase tracking-widest font-extrabold border-[2px] rounded-full ${tone}`}>{status}</span>;
}

function CatPill({ cat }) {
  const tone = CAT_TONE[cat] || "bg-paper border-ink";
  return <span className={`inline-flex px-2.5 py-0.5 text-[10px] uppercase tracking-widest font-extrabold border-[2px] rounded-full ${tone}`}>{cat}</span>;
}

function RowActions({ row, onSetStatus }) {
  return (
    <div className="flex justify-end gap-1.5">
      {row.status !== "reviewed" && (
        <button type="button" data-testid={`feedback-review-${row.id}`} onClick={() => onSetStatus(row.id, "reviewed")} className="w-8 h-8 inline-flex items-center justify-center border-[2.5px] border-ink rounded-full hover:bg-ink hover:text-paper transition-colors" title="Mark reviewed">
          <Check size={13} strokeWidth={2.6} />
        </button>
      )}
      {row.status !== "closed" && (
        <button type="button" data-testid={`feedback-close-${row.id}`} onClick={() => onSetStatus(row.id, "closed")} className="w-8 h-8 inline-flex items-center justify-center border-[2.5px] border-ink rounded-full hover:bg-ink hover:text-paper transition-colors" title="Close / ignore">
          <EyeOff size={13} strokeWidth={2.6} />
        </button>
      )}
      {row.status !== "open" && (
        <button type="button" data-testid={`feedback-reopen-${row.id}`} onClick={() => onSetStatus(row.id, "open")} className="w-8 h-8 inline-flex items-center justify-center border-[2.5px] border-ink rounded-full hover:bg-neon transition-colors" title="Reopen">
          <AlertCircle size={13} strokeWidth={2.6} />
        </button>
      )}
    </div>
  );
}

function DetailPanel({ row, onClose, onSetStatus }) {
  return (
    <div data-testid="feedback-detail" className="fixed inset-0 z-50 flex items-end md:items-center justify-center px-4 py-6 bg-ink/55 backdrop-blur-sm" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-2xl bg-paper border-[3px] border-ink rounded-3xl shadow-pop-lg overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b-[2.5px] border-ink">
          <div className="flex items-center gap-2">
            <CatPill cat={row.category} />
            <StatusPill status={row.status} />
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="feedback-detail-close" className="w-8 h-8 inline-flex items-center justify-center border-[2.5px] border-ink rounded-full hover:bg-ink hover:text-paper transition-colors">
            <X size={15} strokeWidth={2.6} />
          </button>
        </div>

        <div className="p-5 max-h-[70vh] overflow-y-auto">
          <h2 className="font-display text-2xl font-bold tracking-tight leading-tight mb-2">{row.subject}</h2>
          <div className="flex items-center gap-2 text-xs text-ink/60 mb-5">
            <Avatar src={row.reporter?.avatar_url} name={row.reporter?.display_name || row.reporter?.username} size="xs" />
            @{row.reporter?.username || "anon"} · {new Date(row.created_at).toLocaleString()}
          </div>
          <p className="text-[15px] leading-relaxed whitespace-pre-wrap text-ink/85">{row.message}</p>
          {row.screenshot_url && (
            <a href={row.screenshot_url} target="_blank" rel="noreferrer" className="block mt-5 border-[2.5px] border-ink rounded-2xl overflow-hidden">
              <img src={row.screenshot_url} alt="Screenshot" className="w-full" loading="lazy" />
            </a>
          )}
        </div>

        <div className="px-5 py-4 border-t-[2.5px] border-ink flex flex-wrap gap-2 justify-end">
          <button type="button" onClick={() => onSetStatus(row.id, "reviewed")} disabled={row.status === "reviewed"} className="px-4 py-2.5 rounded-full border-[2.5px] border-ink text-[11px] uppercase tracking-widest font-extrabold bg-paper hover:bg-neon disabled:opacity-50">Mark reviewed</button>
          <button type="button" onClick={() => onSetStatus(row.id, "closed")} disabled={row.status === "closed"} className="px-4 py-2.5 rounded-full border-[2.5px] border-ink text-[11px] uppercase tracking-widest font-extrabold bg-paper hover:bg-ink hover:text-paper disabled:opacity-50">Close</button>
        </div>
      </div>
    </div>
  );
}
